<script lang="ts" setup>
import type { Image } from '@/utils/types'

const bannerShow = ref(false)

const props = defineProps({
  banners: {
    type: Array as PropType<Array<{
      image: Image
      category: string
      title: string
      text: string
    }>>,
    default: []
  }
})

onMounted(() => {
  bannerShow.value = true
})
</script>

<template>
  <section class="banner" :class="{ show: bannerShow }">
    <div class="banner__inner">
      <div class="banner__lists" v-for="(item, index) in banners" :key="index">
        <div class="banner__lists__box">
          <figure class="banner__lists__pic">
            <Nimg :src="item.image?.link" :alt="item.image?.alt" :title="item.image?.title" />
          </figure>
          <div class="banner__lists__content">
            <div class="banner__lists__category" v-if="item.category">{{ item.category }}</div>
            <div class="banner__lists__title" v-if="item.title">
              {{ item.title }}
            </div>
          </div>
        </div>
        <div class="banner__lists__text">{{ item.text }}</div>
      </div>
    </div>
  </section>
</template>

<style lang="scss" scoped>
.banner {
  position: relative;
  margin-bottom: toRem(84);
  @include grid;

  @include max-media(990) {
    @include grid(4, 24);
    margin-bottom: toRem(40);
  }

  &__inner {
    grid-column: 2 / span 10;

    @include max-media(990) {
      grid-column: 1 / span 4;
    }
  }

  &.show {
    .banner__lists {
      &__pic {
        img {
          opacity: 1;
          transition-delay: 0.2s;
        }
      }

      &__category,
      &__title {
        opacity: 1;
        transform: translateY(0);
      }

      &__category {
        transition-delay: 0.3s;
      }

      &__title {
        transition-delay: 0.4s;
      }
    }

    .arrow {
      opacity: 1;
      transform: translateY(0);
      transition-delay: 0.5s;
    }
  }

  &__lists {
    &__box {
      position: relative;
    }

    &__pic {
      position: relative;
      z-index: 1;
      background-color: $color-white-1;

      &::before,
      &::after {
        content: '';
        position: absolute;
        z-index: -1;
        width: toRem(168);
        height: toRem(248);
        background-color: $color-brand-4_1;

        @include max-media(990) {
          width: toRem(95);
          height: toRem(73);
        }
      }

      &::before {
        top: toRem(-38);
        left: toRem(-56);

        @include max-media(990) {
          left: toRem(-24);
          top: toRem(-17);
        }
      }

      &::after {
        right: toRem(-60);
        bottom: toRem(-46);

        @include max-media(990) {
          right: toRem(-24);
          bottom: toRem(-17);
        }
      }

      img {
        display: block;
        width: 100%;
        opacity: 0;
        transition: all 0.5s cubic-bezier(0.87, 0, 0.13, 1);
      }
    }

    &__content {
      width: 100%;
      max-width: toRem(660);

      @include min-media(991) {
        position: absolute;
        z-index: 3;
        left: toRem(24);
        bottom: toRem(24);
      }

      @include max-media(990) {
        max-width: 100%;
        padding-top: toRem(44);
      }
    }

    &__text {
      width: toRem(100%);
      max-width: toRem(660);
      padding-left: toRem(24);
      padding-top: toRem(14);
      font-size: toRem(16);
      font-weight: 400;
      line-height: 1.45;

      @include max-media(990) {
        display: none;
      }
    }

    &__category {
      display: inline-block;
      padding: toRem(8) toRem(24);
      background-color: $color-brand-4_1;
      font-size: toRem(16);
      font-weight: 700;
      color: $color-white-1;
      opacity: 0;
      transform: translateY(12px);
      transition: all 0.5s cubic-bezier(0.87, 0, 0.13, 1);

      @include max-media(767) {
        padding: toRem(4) toRem(12);
        font-size: toRem(12);
      }
    }

    &__title {
      padding: toRem(12) toRem(24);
      font-size: toRem(28);
      font-weight: 500;
      line-height: 1.3;
      color: $color-white-1;
      border: 1px solid $color-white-1;
      @include dot-many-line(2);
      opacity: 0;
      transform: translateY(12px);
      transition: all 0.5s cubic-bezier(0.87, 0, 0.13, 1);

      @include max-media(990) {
        border-color: $color-gray-line;
        padding: toRem(12);
        font-size: toRem(22);
        color: $color-black-1;
      }
    }
  }

  .arrow {
    opacity: 0;
    transform: translateY(12px);
    transition: all 0.5s cubic-bezier(0.87, 0, 0.13, 1);

    @include min-media(991) {
      position: absolute;
      right: toRem(24);
      bottom: toRem(24);
      z-index: 10;
    }

    @include max-media(990) {
      padding-right: toRem(24);
    }

    &__prev {
      margin-right: toRem(48);

      @include max-media(990) {
        margin-right: toRem(12);
      }
    }
  }
}
</style>
